import ColumnText from "../molecules/ColumnText";
import { Fragment } from "react";
import { Empty } from "./Empty";
import { PlusIcon } from "@heroicons/react/20/solid";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { t } from "i18next";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { TableLoader } from "../molecules/TableLoader";
import { DateTime } from "luxon";
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";
import { toasts } from "@/shared/utils/toasts";

type TareaProps = {
  id: number;
  tarea: string;
  fecha: string;
  asignado: string;
  urgencia: string;
  state: number;
  task: string;
  date: string;
  urgency: number;
  assigned: {
    name: string;
  };
  favorite: any;
};

export type TableListMaintenanceProps = {
  isLoading: boolean;
  maintenances: TareaProps[];
  title: string;
  description: string;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  search: string;
  onFavorite: (maintenance: any) => void;
  functionButtonCreate: () => void;
  functionButtonEdit: (maintenance: any) => void;
  filterButton: () => void;
  functionButtonInventory: () => void;
  onStatusChange?: (maintenance: TareaProps) => void;
  canViewInventory: boolean;
  canCreateMaintenance: boolean;
  canEditMaintenance: boolean;
  canCompleteMaintenance: boolean;
  canCreateInventory: boolean;
};

const TableListMaintenance: React.FC<TableListMaintenanceProps> = ({
  isLoading,
  onFavorite,
  title,
  description,
  maintenances,
  functionButtonCreate,
  functionButtonEdit,
  filterButton,
  functionButtonInventory,
  handleChange,
  search,
  onStatusChange,
  canViewInventory,
  canCreateMaintenance,
  canEditMaintenance,
  canCompleteMaintenance,
  canCreateInventory,
}) => {
  const onClickEmpty = () => {
    if (canCreateInventory) {
      return functionButtonCreate();
    }

    toasts.warning("Oops!", "No tienes permisos para crear un mantenimiento");
  };

  return (
    <div className="flex-1 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="sm:flex sm:items-center">
        <div className="flex w-full md:w-72">
          <InputWithCornerHint
            label={t("general.search")}
            span=""
            id=""
            name="search"
            type=""
            className="w-full"
            placeholder={t("general.search")}
            error={false}
            onChange={handleChange}
            value={search}
          />
          <button
            type="button"
            className="sm:ml-2 mt-5 p-2 text-gray-400 hover:text-gray-500"
            onClick={filterButton}
          >
            <span className="sr-only">View KPIs</span>
            <FontAwesomeIcon
              icon={faFilter}
              aria-hidden="true"
              className="h-6 w-6"
            />
          </button>
        </div>
        <div className="mt-8 sm:ml-auto sm:mt-4 flex items-center space-x-4">
          {canCreateMaintenance && (
            <div>
              <button
                onClick={functionButtonCreate}
                type="button"
                className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
              >
                <PlusIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          )}

          {canViewInventory && (
            <ButtonSecondary
              text={t("maintenance.views.inventory")}
              onClick={functionButtonInventory}
              size="large"
            />
          )}
        </div>
      </div>

      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <TableLoader columns={9} />
            ) : (
              <Fragment>
                {maintenances.length === 0 ? (
                  <Empty
                    texto={t("maintenance.views.no_task")}
                    onClick={onClickEmpty}
                  />
                ) : (
                  <Fragment>
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                          ></th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("maintenance.views.task")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("maintenance.views.date")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("maintenance.views.assigned_to")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("maintenance.views.urgency")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("maintenance.views.status")}
                          </th>
                          <th
                            scope="col"
                            className="text-center text-sm font-semibold text-gray-900"
                          >
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {maintenances.map((maintenance) => {
                          return (
                            <tr key={maintenance.id}>
                              <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                                {maintenance.favorite ? (
                                  <FontAwesomeIcon
                                    icon={faStarSolid}
                                    onClick={() => onFavorite(maintenance)}
                                  />
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faStarRegular}
                                    onClick={() => onFavorite(maintenance)}
                                  />
                                )}
                              </td>
                              <ColumnText title={maintenance.task} />
                              <ColumnText
                                title={DateTime.fromFormat(
                                  maintenance.date,
                                  "yyyy-MM-dd"
                                ).toFormat("dd-MM-yyyy")}
                              />
                              <ColumnText title={maintenance.assigned?.name} />
                              <ColumnText
                                title={
                                  <Fragment>
                                    {maintenance.urgency == 1 &&
                                      t("maintenance.views.urgency_low")}
                                    {maintenance.urgency == 2 &&
                                      t("maintenance.views.urgency_medium")}
                                    {maintenance.urgency == 3 &&
                                      t("maintenance.views.urgency_high")}
                                  </Fragment>
                                }
                              />
                              <ColumnText
                                title={
                                  <Fragment>
                                    {maintenance.state === 0 &&
                                      t("maintenance.views.status_pending")}
                                    {maintenance.state === 1 &&
                                      t("maintenance.views.status_complete")}
                                  </Fragment>
                                }
                              />
                              <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                <div className="flex items-center justify-end space-x-2">
                                  {canCompleteMaintenance &&
                                    maintenance.state === 0 && (
                                      <button
                                        role="button"
                                        type="button"
                                        className="text-primary hover:text-primary-900"
                                        onClick={() =>
                                          onStatusChange?.(maintenance)
                                        }
                                      >
                                        Completar
                                      </button>
                                    )}

                                  {canEditMaintenance && (
                                    <button
                                      type="button"
                                      role="button"
                                      className="text-primary hover:text-primary-900"
                                      onClick={() =>
                                        functionButtonEdit(maintenance)
                                      }
                                    >
                                      {t("general.edit")}
                                      <span className="sr-only"></span>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Fragment>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableListMaintenance;
